import { IS_BACKEND_MODE, platform } from "../../app/browser-platform.js";
import { CREDENTIAL_FAILURE_MESSAGES, type CredentialFailure } from "../../domain/identity/credential-authentication.js";
import { RoleCode } from "../../domain/identity/access-types.js";

/**
 * PORTA ÚNICA DE AUTENTICAÇÃO DA INTERFACE — decide o transporte uma vez:
 *
 *   HOMOLOGAÇÃO (IS_BACKEND_MODE)  → POST /api/login, GET /api/session,
 *                                    POST /api/logout (servidor Express).
 *   TESTE LOCAL (`npm run dev`)    → autenticação local pelo MESMO
 *                                    repositório do cadastro de Usuários
 *                                    (AdminService.authenticateByCredentials).
 *
 * Sem isto, a tela de login chamava /api/login sempre: no teste local o Vite
 * não tem essa rota, o POST voltava 404 com corpo vazio e a tela exibia
 * "Usuário não encontrado ou senha incorreta" para qualquer credencial —
 * inclusive as corretas. A regra de allowlist de homologação continua
 * exclusiva do servidor: nada dela roda no caminho local.
 */

export interface AuthenticatedSession {
  token: string;
  userId: string;
  name: string;
  role: string;
  professionalId: string | null;
  customPermissions?: readonly string[];
}

export type AuthResult =
  | { ok: true; session: AuthenticatedSession }
  | { ok: false; message: string; status: number };

const TOKEN_KEY = "inspect_auth_token";
const USER_KEY = "inspect_user_id";
const LOCAL_TOKEN_PREFIX = "local.";
const LOCAL_SESSION_KEY = "inspect_local_session";
/** Sessão local expira em 12h — uma aba esquecida não vale para sempre. */
const LOCAL_SESSION_TTL_MS = 12 * 60 * 60 * 1000;

const read = (key: string): string | null => {
  try { return localStorage.getItem(key); } catch { return null; }
};
const write = (key: string, value: string): void => {
  try { localStorage.setItem(key, value); } catch { /* armazenamento indisponível */ }
};
const drop = (key: string): void => {
  try { localStorage.removeItem(key); } catch { /* armazenamento indisponível */ }
};

export const isBackendMode = (): boolean => IS_BACKEND_MODE;

/**
 * TOKEN DO AMBIENTE LOCAL — E O QUE ELE NÃO É.
 *
 * O token local era `local.<userId>`: o id do usuário em texto. Bastava
 * escrever `local.usr_admin` na chave do navegador e recarregar para entrar
 * como o administrador, sem senha. Agora é um valor aleatório opaco, ligado
 * ao usuário por um registro de sessão com validade.
 *
 * LIMITE HONESTO: no teste local (`npm run dev`) não existe servidor, então
 * o registro de sessão mora no mesmo armazenamento que a pessoa à frente do
 * devtools controla. Quem quiser forjar uma sessão ainda consegue — o que
 * mudou é que não dá mais para fazê-lo por acidente nem adivinhando um id, e
 * que uma sessão abandonada caduca. A fronteira de segurança de verdade é o
 * backend (`IS_BACKEND_MODE`), onde o token é emitido e conferido pelo
 * servidor (`DatabaseSessionService`) e o contexto é remontado em
 * `/api/rpc`. Nada aqui substitui isso.
 */
interface LocalSessionRecord { token: string; userId: string; expiresAt: number }

const newLocalToken = (): string => {
  const bytes = new Uint8Array(32);
  (globalThis as { crypto: Crypto }).crypto.getRandomValues(bytes);
  return LOCAL_TOKEN_PREFIX + Array.from(bytes, b => b.toString(16).padStart(2, "0")).join("");
};

function rememberLocalSession(token: string, userId: string): void {
  const record: LocalSessionRecord = { token, userId, expiresAt: Date.now() + LOCAL_SESSION_TTL_MS };
  write(LOCAL_SESSION_KEY, JSON.stringify(record));
}

/** Devolve o usuário desta sessão local, ou null se o token não confere ou caducou. */
function userIdFromLocalSession(token: string): string | null {
  const raw = read(LOCAL_SESSION_KEY);
  if (!raw) return null;
  try {
    const record = JSON.parse(raw) as LocalSessionRecord;
    if (!record || record.token !== token || !record.userId) return null;
    if (!Number.isFinite(record.expiresAt) || Date.now() > record.expiresAt) {
      drop(LOCAL_SESSION_KEY);
      return null;
    }
    return String(record.userId);
  } catch {
    return null;
  }
}

const sessionOfUser = (user: any, professionalId: string | null, token: string, plhPermissions: readonly string[] = []): AuthenticatedSession => {
  // As permissões de PLH são DERIVADAS de quem o Cliente nomeou responsável
  // (`Client.responsiblePlhProfessionalId`), não gravadas na conta: substituir
  // o PLH do Cliente tem de retirar a capacidade do anterior na sessão
  // seguinte, sem ninguém precisar lembrar de editar permissões.
  const custom = [...(user.customPermissions ?? []), ...plhPermissions];
  return {
    token,
    userId: String(user.id),
    name: user.name || user.login || String(user.id),
    role: user.profileType || RoleCode.Viewer,
    professionalId,
    ...(custom.length > 0 ? { customPermissions: [...new Set(custom.map(String))] } : {})
  };
};

/**
 * As permissões de PLH desta pessoa, derivadas de quem o Cliente nomeou
 * responsável. Vazio quando ela não é PLH de nenhum Cliente ativo, ou quando
 * não está habilitada (inativa, não Engenheira, sem assinatura cadastrada).
 */
async function plhPermissionsOf(professionalId: string | null): Promise<readonly string[]> {
  if (!professionalId) return [];
  try {
    return await (platform as any).adminService.plhPermissionsForAuthentication(professionalId) ?? [];
  } catch {
    return [];
  }
}

/** Professional vinculado ao usuário — pelos dois sentidos do vínculo (User.professionalId e Professional.userId). */
async function localProfessionalIdOf(user: any): Promise<string | null> {
  if (user.professionalId) return String(user.professionalId);
  try {
    const professionals: any[] = await (platform as any).adminService.listProfessionalsForAuthentication?.(String(user.id));
    if (Array.isArray(professionals) && professionals[0]) return String(professionals[0].id);
  } catch { /* segue sem profissional */ }
  return null;
}

async function loginLocally(identifier: string, password: string): Promise<AuthResult> {
  const result = await (platform as any).adminService.authenticateByCredentials(identifier, password);
  if (!result.user) {
    const failure: CredentialFailure = result.failure || "INVALID_CREDENTIALS";
    return { ok: false, status: failure === "USER_INACTIVE" ? 403 : 401, message: CREDENTIAL_FAILURE_MESSAGES[failure] };
  }
  const professionalId = await localProfessionalIdOf(result.user);
  const token = newLocalToken();
  rememberLocalSession(token, String(result.user.id));
  return { ok: true, session: sessionOfUser(result.user, professionalId, token, await plhPermissionsOf(professionalId)) };
}

async function loginThroughBackend(identifier: string, password: string): Promise<AuthResult> {
  let res: Response;
  try {
    res = await fetch("/api/login", {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ username: identifier, password }),
      credentials: "include"
    });
  } catch (e) {
    console.error("Falha de rede no login:", e);
    return { ok: false, status: 0, message: "Não foi possível falar com o servidor. Verifique sua conexão e tente novamente." };
  }
  const data = await res.json().catch(() => ({} as any));
  if (!res.ok) {
    if (res.status >= 500) {
      console.error("Erro de servidor no login:", res.status, data.error);
      return { ok: false, status: res.status, message: "Não foi possível realizar o acesso. Tente novamente ou contate o administrador." };
    }
    return { ok: false, status: res.status, message: data.error || CREDENTIAL_FAILURE_MESSAGES.INVALID_CREDENTIALS };
  }
  return { ok: true, session: sessionFromServer(data.token, data) };
}

/**
 * Sessão como o servidor a descreve — inclusive `customPermissions`, que
 * antes era descartado aqui. O efeito era visível: permissões concedidas
 * individualmente a um usuário funcionavam no teste local e sumiam em
 * homologação (e sumiam no F5, porque o restore passava pelo mesmo ponto).
 */
function sessionFromServer(token: string, data: any): AuthenticatedSession {
  const custom = Array.isArray(data.customPermissions) ? data.customPermissions.map(String) : null;
  return {
    token,
    userId: String(data.userId),
    name: data.name,
    role: data.role || RoleCode.Viewer,
    professionalId: data.professionalId ?? null,
    ...(custom && custom.length > 0 ? { customPermissions: custom } : {})
  };
}

export async function login(identifier: string, password: string): Promise<AuthResult> {
  const result = IS_BACKEND_MODE ? await loginThroughBackend(identifier, password) : await loginLocally(identifier, password);
  if (result.ok) {
    if (!IS_BACKEND_MODE) {
      write(TOKEN_KEY, result.session.token);
      write(USER_KEY, result.session.userId);
    }
  }
  return result;
}

/**
 * Sessão guardada, revalidada na origem: no servidor pelo GET /api/session (via Cookie HTTP HttpOnly);
 * no teste local relendo o usuário no repositório (excluído ou inativado
 * depois do login → sessão cai).
 */
export async function restoreSession(): Promise<AuthenticatedSession | null> {
  if (!IS_BACKEND_MODE) {
    const token = read(TOKEN_KEY);
    if (!token) return null;
    // Só o registro de sessão diz de quem é o token. Antes havia uma queda
    // para `inspect_user_id` — outra chave do navegador —, de modo que um
    // token qualquer somado a um id escrito à mão restaurava a sessão.
    const userId = userIdFromLocalSession(token);
    if (!userId) return null;
    try {
      const user = await (platform as any).adminService.findUserForAuthentication(userId);
      if (!user) return null;
      // Recalculado a cada restauração (inclusive no F5): se o Cliente trocou
      // de PLH enquanto a sessão existia, a capacidade acompanha.
      const professionalId = await localProfessionalIdOf(user);
      return sessionOfUser(user, professionalId, token, await plhPermissionsOf(professionalId));
    } catch (e) {
      console.warn("Não foi possível restaurar a sessão local:", e);
      return null;
    }
  }

  const res = await fetch("/api/session", {
    cache: "no-store",
    credentials: "include"
  });
  if (!res.ok) {
    const error = new Error("Invalid session") as Error & { status?: number };
    error.status = res.status;
    throw error;
  }
  const data = await res.json();
  return sessionFromServer(data.token || "", data);
}

export async function logout(): Promise<void> {
  if (IS_BACKEND_MODE) {
    try {
      await fetch("/api/logout", {
        method: "POST",
        credentials: "include"
      });
    } catch { /* sessão no servidor tratada */ }
  }
  drop(TOKEN_KEY);
  drop(USER_KEY);
  drop(LOCAL_SESSION_KEY);
}
